import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, useAuth, useOnline } from "@/lib/api";
import { useLang, LANG_META } from "@/lib/i18n";
import RegionSwitcher from "@/components/RegionSwitcher";
import { LogoMark } from "@/components/Logo";
import WelcomeTour from "@/components/WelcomeTour";
import ShareApp from "@/components/ShareApp";
import { LogOut, WifiOff, Wifi, ClipboardList, LayoutDashboard, GraduationCap, Video, PiggyBank, Briefcase, Globe2, Building2, Home, User, Bell, Search, Award, BookOpen, CalendarClock, AlertTriangle, Clock, MessageSquare } from "lucide-react";

const ROLE_HOME = { trainee: "/app", assessor: "/assessor", admin: "/admin", superadmin: "/superadmin", employer: "/employer" };
const ROLE_LABEL = { trainee: "Student", assessor: "Instructor", admin: "Program Admin", superadmin: "Super Admin", employer: "Employer" };
const NOTE_ICONS = { award: <Award size={15} className="text-amber-500" />, alert: <AlertTriangle size={15} className="text-red-500" />, clock: <Clock size={15} className="text-sky-500" />, book: <BookOpen size={15} className="text-teal-600" />, calendar: <CalendarClock size={15} className="text-violet-500" />, briefcase: <Briefcase size={15} className="text-emerald-600" />, video: <Video size={15} className="text-teal-600" />, message: <MessageSquare size={15} className="text-teal-700" /> };

export const LangSwitch = () => {
  const { lang, setLang, langs } = useLang();
  return (
    <div className="flex rounded-full border border-slate-200 overflow-hidden text-xs font-semibold" data-testid="language-switch">
      {langs.map((l) => (
        <button key={l} data-testid={`lang-${l}-btn`} onClick={() => setLang(l)}
          className={`px-2.5 py-1.5 min-w-[40px] ${lang === l ? "bg-teal-800 text-white" : "bg-white text-slate-500"}`}>
          {LANG_META[l].label}
        </button>
      ))}
    </div>
  );
};

function Bell_({ user }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/me/notifications").then((r) => setNotes(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [user]);
  return (
    <div className="relative">
      <button data-testid="notifications-bell" onClick={() => setOpen(!open)} className="relative p-2 rounded-full text-slate-500 hover:bg-slate-100">
        <Bell size={18} />
        {notes.length > 0 && <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{notes.length}</span>}
      </button>
      {open && (
        <div data-testid="notifications-panel" className="absolute right-0 mt-2 w-80 bg-white rounded-2xl border border-slate-200 shadow-2xl p-2 z-50 fade-in">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 px-3 py-2">Notifications</p>
          <div className="max-h-80 overflow-y-auto">
            {notes.length === 0 && <p className="text-sm text-slate-400 px-3 py-4">You're all caught up 🎉</p>}
            {notes.map((n) => (
              <div key={n.id} data-testid={`notification-${n.id}`} onClick={() => { if (n.link) { setOpen(false); navigate(n.link); } }}
                className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl hover:bg-slate-50 ${n.link ? "cursor-pointer" : ""}`}>
                <span className="mt-0.5 shrink-0">{NOTE_ICONS[n.icon] || NOTE_ICONS.book}</span>
                <div className="min-w-0"><p className="text-[13px] text-slate-700 leading-snug">{n.text}</p><p className="text-[10px] text-slate-400 mt-0.5 capitalize">{n.time}</p></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const online = useOnline();
  const navigate = useNavigate();
  const loc = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/messages/unread-count").then((r) => setUnread(r.data.unread)).catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [user, loc.pathname]);

  const navItem = (to, icon, label, testid, badge = 0) => (
    <Link to={to} data-testid={testid}
      className={`relative flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[13px] font-semibold transition-colors whitespace-nowrap ${loc.pathname === to ? "bg-teal-800 text-white shadow-sm" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"}`}>
      {icon}<span className="hidden sm:inline">{label}</span>
      {badge > 0 && <span data-testid={`${testid}-badge`} className="absolute -top-1 -right-1 w-4.5 h-4.5 min-w-[18px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{badge}</span>}
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#F7F9FB]">
      {!online && (
        <div data-testid="offline-banner" className="bg-slate-900 text-slate-100 text-xs py-2 px-4 flex items-center gap-2 sticky top-0 z-50">
          <WifiOff size={14} /> {t("offline")} · {t("syncPending")}
        </div>
      )}
      <header className="bg-white border-b border-slate-200/80 sticky top-0 z-40 shadow-[0_1px_8px_rgba(15,23,42,0.04)]">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-2.5 flex items-center justify-between gap-3">
          <Link to={user ? ROLE_HOME[user.role] || "/app" : "/"} data-testid="home-link" className="flex items-center gap-2 shrink-0">
            <LogoMark size={36} />
            <span className="leading-tight hidden xs:block sm:block">
              <span className="block font-heading font-extrabold text-slate-900 text-[15px]">{t("appName")}</span>
              <span className="block text-[9px] uppercase tracking-[0.12em] text-teal-700/70">{user ? ROLE_LABEL[user.role] : ""} Workspace</span>
            </span>
          </Link>
          <div className="hidden lg:flex flex-1 max-w-md">
            <div className="relative w-full">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="global-search-input" placeholder="Search courses, lessons, candidates…"
                onKeyDown={(e) => { if (e.key === "Enter") navigate(user?.role === "trainee" ? "/app" : ROLE_HOME[user?.role] || "/app"); }}
                className="w-full h-10 pl-9 pr-3 rounded-xl bg-slate-100/80 border border-transparent focus:bg-white focus:border-teal-500 focus:outline-none text-sm transition-colors" />
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span data-testid="online-indicator" title={online ? "Online" : "Offline"}
              className={`hidden sm:flex items-center ${online ? "text-emerald-500" : "text-slate-300"}`}>
              {online ? <Wifi size={16} /> : <WifiOff size={16} />}
            </span>
            <RegionSwitcher />
            <LangSwitch />
            {user && <ShareApp compact />}
            {user && <Bell_ user={user} />}
            {user && (
              <div className="hidden md:flex items-center gap-2 pl-2 ml-1 border-l border-slate-200">
                <span className="w-8 h-8 rounded-full bg-teal-800 text-white flex items-center justify-center font-heading font-bold text-xs">{user.name?.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
                <span className="leading-tight">
                  <span className="block text-[13px] font-semibold text-slate-800 max-w-[120px] truncate">{user.name}</span>
                  <span className="block text-[10px] text-slate-400">{ROLE_LABEL[user.role]}</span>
                </span>
              </div>
            )}
            {user && (
              <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
                className="p-2 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700" title={t("logout")}>
                <LogOut size={17} />
              </button>
            )}
          </div>
        </div>
        {user && (
          <nav className="max-w-7xl mx-auto px-4 lg:px-6 pb-2 flex gap-1 overflow-x-auto">
            {user.role === "trainee" && (
              <>
                {navItem("/app", <GraduationCap size={15} />, t("tracks"), "nav-tracks")}
                {navItem("/live", <Video size={15} />, "Live", "nav-live")}
                {navItem("/finlab", <PiggyBank size={15} />, "Money Skills", "nav-finlab")}
                {navItem("/career", <Briefcase size={15} />, "Career", "nav-career")}
                {navItem("/applications", <ClipboardList size={15} />, t("applications"), "nav-applications")}
              </>
            )}
            {(user.role === "assessor" || user.role === "admin" || user.role === "superadmin") && (
              <>
                {navItem("/assessor", <ClipboardList size={15} />, t("assessorConsole"), "nav-assessor")}
                {navItem("/builder", <GraduationCap size={15} />, "Course Builder", "nav-builder")}
                {navItem("/students", <User size={15} />, "Students", "nav-students")}
              </>
            )}
            {navItem("/messages", <MessageSquare size={15} />, "Messages", "nav-messages", unread)}
            {(user.role === "admin" || user.role === "superadmin") && navItem("/admin", <LayoutDashboard size={15} />, t("adminDashboard"), "nav-admin")}
            {user.role === "superadmin" && navItem("/superadmin", <Globe2 size={15} />, "Global", "nav-superadmin")}
            {(user.role === "employer" || user.role === "superadmin") && navItem("/employer", <Building2 size={15} />, "Talent", "nav-employer")}
          </nav>
        )}
      </header>
      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-6 space-y-6 fade-in pb-24 sm:pb-10">{children}</main>
      <WelcomeTour />
      {user?.role === "trainee" && (
        <nav data-testid="bottom-nav" className="sm:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-slate-200 grid grid-cols-6">
          {[["/app", <Home size={19} />, "Home", "bnav-home"], ["/live", <Video size={19} />, "Live", "bnav-live"], ["/finlab", <PiggyBank size={19} />, "Money", "bnav-lab"], ["/messages", <MessageSquare size={19} />, "Chat", "bnav-messages"], ["/career", <Briefcase size={19} />, "Career", "bnav-career"], ["/applications", <User size={19} />, "Profile", "bnav-profile"]].map(([to, icon, label, tid]) => (            <Link key={to} to={to} data-testid={tid}
              className={`relative flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold ${loc.pathname === to ? "text-teal-700" : "text-slate-400"}`}>
              {icon}{label}
              {to === "/messages" && unread > 0 && <span className="absolute top-1 right-1/2 translate-x-4 w-4 h-4 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center">{unread}</span>}
            </Link>
          ))}
        </nav>
      )}
      <footer className="max-w-7xl mx-auto px-4 lg:px-6 py-6 text-[11px] text-slate-400">{t("internalNote")}</footer>
    </div>
  );
}
